import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Search as SearchIcon, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { inputClass } from "@/components/Panel";
import { Badge } from "@/components/ui";
import { itemStockQuery, listStock, stockListQuery, type StockRow } from "@/features/inventory/api";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { ItemStockPanel } from "@/features/inventory/ItemStockPanel";
import { itemDetailQuery } from "@/features/items/api";
import { warehouseListQuery } from "@/features/warehousing/api";
import { cn } from "@/lib/cn";
import { useFormat } from "@/lib/format";
import { itemTypes, stockListColumns } from "@patronix/domain/list";
import { listView, noView, viewQuery, viewSearch, type ViewSearch } from "@/lib/listView";

// Stock overview: items with on-hand <> 0, optionally in one warehouse.
// One RPC per page (keyset on item code), one per item detail (prefetched on
// hover, together with the item master). Warehouse filter reuses the cached
// warehouse list.

interface Search extends ViewSearch {
  wh: string;
  q: string;
  item?: string | undefined;
}

export const Route = createFileRoute("/_app/warehousing/inventory")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {
      wh: typeof raw.wh === "string" ? raw.wh : "",
      q: typeof raw.q === "string" ? raw.q.slice(0, 100) : "",
    };
    if (typeof raw.item === "string" && raw.item) s.item = raw.item;
    return { ...s, ...viewSearch(stockListColumns, raw) };
  },
  loaderDeps: ({ search }) => ({ wh: search.wh, q: search.q, f: search.f }),
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchInfiniteQuery(warehouseListQuery(false));
    return context.queryClient.ensureInfiniteQueryData(stockListQuery({ warehouseId: deps.wh, q: deps.q, filters: viewQuery(stockListColumns, deps).filters }));
  },
  component: InventoryPage,
});

const typeTone = {
  material: "neutral",
  semi_finished: "violet",
  finished_good: "success",
  service: "info",
} as const;

function InventoryPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();

  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const view = listView(stockListColumns, search, setSearch);
  const params = { warehouseId: search.wh, q: search.q, filters: view.query.filters };
  const query = useInfiniteQuery(stockListQuery(params));
  const warehouses = useInfiniteQuery(warehouseListQuery(false)).data?.pages.flatMap((p) => p.rows) ?? [];

  const columns: Column<StockRow>[] = [
    {
      key: "code",
      header: t("stock.colItem"),
      width: "minmax(110px,0.8fr)",
      cell: (r, selected) => (
        <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.code}</span>
      ),
    },
    { key: "description", header: t("stock.colDescription"), width: "minmax(200px,2fr)", cell: (r) => r.description },
    {
      key: "type",
      header: t("items.colType"),
      width: "minmax(120px,0.9fr)",
      cell: (r) => <Badge tone={typeTone[r.item_type]}>{t(`itemType.${r.item_type}`)}</Badge>,
      options: itemTypes.map((v) => ({ value: v, label: t(`itemType.${v}`) })),
    },
    {
      key: "where",
      header: t("stock.colWhere"),
      width: "minmax(120px,0.8fr)",
      className: "text-ink-muted",
      cell: (r) => t("stock.locationsCount", { count: r.locations }),
    },
    {
      key: "onHand",
      header: t("stock.colOnHand"),
      width: "minmax(130px,0.9fr)",
      align: "end",
      cell: (r) => (
        <span className={cn("tabular font-medium", Number(r.on_hand) < 0 && "text-(--status-danger-fg)")}>
          {fmt.qtyIn(r.on_hand, r.base_uom_decimals)} <span className="font-normal text-ink-muted">{r.base_uom_code}</span>
          {Number(r.project_on_hand ?? 0) !== 0 && (
            <span className="block text-[11px] font-normal text-(--mod-project-ink)">
              {t("stock.ofWhichProject", { qty: fmt.qtyIn(r.project_on_hand ?? 0, r.base_uom_decimals) })}
            </span>
          )}
        </span>
      ),
    },
    {
      key: "reserved",
      header: t("stock.colReserved"),
      width: "minmax(110px,0.8fr)",
      align: "end",
      className: "text-ink-muted",
      cell: (r) => (Number(r.reserved) === 0 ? "—" : <span className="tabular">{fmt.qtyIn(r.reserved, r.base_uom_decimals)}</span>),
    },
    {
      key: "available",
      header: t("stock.colAvailable"),
      width: "minmax(120px,0.8fr)",
      align: "end",
      cell: (r) => (
        <span className={cn("tabular", Number(r.available) < 0 && "font-medium text-(--status-danger-fg)")}>
          {fmt.qtyIn(r.available, r.base_uom_decimals)}
        </span>
      ),
    },
  ];

  const warehouseCode = warehouses.find((w) => w.id === search.wh)?.code ?? "";
  const exportColumns: ExportColumn<StockRow>[] = [
    { header: t("stock.colItem"), kind: "code", value: (r) => r.code },
    { header: t("stock.colDescription"), kind: "text", value: (r) => r.description },
    { header: t("items.colType"), kind: "code", value: (r) => r.item_type },
    { header: t("export.warehouse"), kind: "code", value: () => warehouseCode || t("export.allWarehouses") },
    { header: t("export.locations"), kind: "integer", value: (r) => r.locations },
    { header: t("stock.colOnHand"), kind: "number", value: (r) => r.on_hand, unit: (r) => r.base_uom_code },
    { header: t("stock.colProject"), kind: "number", value: (r) => r.project_on_hand ?? 0, unit: (r) => r.base_uom_code },
    { header: t("stock.colReserved"), kind: "number", value: (r) => r.reserved, unit: (r) => r.base_uom_code },
    { header: t("stock.colAvailable"), kind: "number", value: (r) => r.available, unit: (r) => r.base_uom_code },
    { header: t("items.colUom"), kind: "code", value: (r) => r.base_uom_code },
  ];

  const listTools: ListTools<StockRow> = {
    id: "stock",
    title: t("stock.title"),
    export: { fileBase: "stock", sheet: t("stock.title"), columns: exportColumns, fetchPage: (cursor, signal) => listStock(params, cursor, signal, EXPORT_PAGE) },
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.warehousing.title")} <span aria-hidden="true">/</span> {t("nav.warehousing.inventory")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("stock.title")}</h1>
        </div>
      </header>

      <Toolbar
        search={search}
        setSearch={setSearch}
        warehouses={warehouses.map((w) => ({ id: w.id, label: `${w.company?.code ?? ""} · ${w.code} — ${w.name}` }))}
      />

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("stock.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.item_id}
          selectedId={search.item}
          onSelect={(r) => setSearch({ item: r.item_id })}
          onClear={() => setSearch({ item: undefined })}
          onPrefetch={(r) => {
            void queryClient.prefetchQuery(itemStockQuery(r.item_id));
            void queryClient.prefetchQuery(itemDetailQuery(r.item_id));
          }}
          emptyTitle={search.q || search.wh ? t("stock.emptyFilteredTitle") : t("stock.emptyTitle")}
          emptyBody={search.q || search.wh ? t("stock.emptyFilteredBody") : t("stock.emptyBody")}
          emptyAction={
            search.q || search.wh ? (
              <button
                type="button"
                onClick={() => setSearch({ wh: "", q: "", item: undefined, ...noView })}
                className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
              >
                {t("list.clearFilters")}
              </button>
            ) : undefined
          }
          minWidth={920}
          tools={listTools}
          view={view}
        />
        <DetailPanel
          label={t("stock.detailLabel")}
          open={!!search.item}
          placeholder={<DetailPlaceholder title={t("stock.selectPrompt")} hint={t("stock.selectPromptHint")} />}
        >
          {search.item && <ItemStockPanel key={search.item} itemId={search.item} onClose={() => setSearch({ item: undefined })} />}
        </DetailPanel>
      </div>
    </div>
  );
}

function Toolbar({
  search,
  setSearch,
  warehouses,
}: {
  search: Search;
  setSearch: (p: Partial<Search>) => void;
  warehouses: { id: string; label: string }[];
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(search.q);

  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label={t("locations.warehouse")}
        value={search.wh}
        onChange={(e) => setSearch({ wh: e.target.value, item: undefined })}
        className={cn(inputClass, "w-auto min-w-[240px] font-medium")}
      >
        <option value="">{t("stock.allWarehouses")}</option>
        {warehouses.map((w) => (
          <option key={w.id} value={w.id}>
            {w.label}
          </option>
        ))}
      </select>
      <div className="grow" />
      <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
        <SearchIcon size={15} aria-hidden="true" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("items.searchPlaceholder")}
          aria-label={t("items.searchPlaceholder")}
          data-list-search
          className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
        />
        {text && (
          <button type="button" aria-label={t("list.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
            <X size={14} />
          </button>
        )}
      </label>
    </div>
  );
}
